import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  TablePagination,
  Typography,
  Box,
  Chip,
  Tooltip,
} from "@mui/material";
import { Unarchive as UnarchiveIcon } from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { Class } from "../../../../redux/types/types";
import ArchiveConfirmDialog from "../ArchiveConfirmDialog/ArchiveConfirmDialog";

interface ArchivedClassesListProps {
  classes: Class[];
  totalClasses: number;
  page: number;
  rowsPerPage: number;
  orderBy: keyof Class | any;
  order: "asc" | "desc";
  onRequestSort: (property: keyof Class) => void;
  onChangePage: (event: unknown, newPage: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUnarchiveClass: (classId: number) => void;
}

const languages =
  process.env.REACT_APP_DEFAULT_LANGUAGE === "en"
    ? (["en", "uk", "fr"] as const)
    : (["fr", "uk", "en"] as const);
type Language = (typeof languages)[number];

const LocalizedContent: React.FC<{ content: Record<Language, string> }> = ({
  content,
}) => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>
    {languages.map(
      (lang) =>
        content[lang] && (
          <Box
            key={lang}
            sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
          >
            <Chip
              label={lang.toUpperCase()}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ width: 40, mr: 1, flexShrink: 0 }}
            />
            <Typography variant="body2" component="span">
              {content[lang]}
            </Typography>
          </Box>
        )
    )}
  </Box>
);

export default function ArchivedClassesList({
  classes,
  totalClasses,
  page,
  rowsPerPage,
  orderBy,
  order,
  onRequestSort,
  onChangePage,
  onChangeRowsPerPage,
  onUnarchiveClass,
}: ArchivedClassesListProps) {
  const intl = useIntl();
  const [unarchiveDialogOpen, setUnarchiveDialogOpen] = useState(false);
  const [classToUnarchive, setClassToUnarchive] = useState<Class | null>(null);
  const [unarchiveError, setUnarchiveError] = useState<string | null>(null);

  const createSortHandler =
    (property: keyof Class) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(property);
    };

  const handleUnarchiveClick = (classItem: Class) => {
    setClassToUnarchive(classItem);
    setUnarchiveDialogOpen(true);
  };

  const handleUnarchiveDialogClose = () => {
    setUnarchiveDialogOpen(false);
    setClassToUnarchive(null);
    setUnarchiveError(null);
  };

  const handleUnarchiveConfirm = () => {
    if (classToUnarchive) {
      onUnarchiveClass(classToUnarchive.id);
      handleUnarchiveDialogClose();
    }
  };

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "title"}
                  direction={orderBy === "title" ? order : "asc"}
                  onClick={createSortHandler("title")}
                >
                  <FormattedMessage id="admin.dashboard.class.title" />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <FormattedMessage id="admin.dashboard.class.instructors" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="admin.dashboard.actions" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {classes.map((classItem) => (
              <TableRow key={classItem.id}>
                <TableCell>
                  <LocalizedContent content={classItem.title} />
                </TableCell>
                <TableCell>
                  {classItem.instructors?.map((ci, index) => (
                    <Typography key={ci.instructor.id} variant="body2">
                      {index > 0 && ", "}
                      {ci.instructor.firstname} {ci.instructor.lastname}
                    </Typography>
                  ))}
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={intl.formatMessage({
                      id: "admin.dashboard.unarchiveClass",
                    })}
                  >
                    <IconButton
                      color="primary"
                      onClick={() => handleUnarchiveClick(classItem)}
                      size="small"
                    >
                      <UnarchiveIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalClasses}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        labelRowsPerPage={<FormattedMessage id="admin.dashboard.rowsPerPage" />}
      />
      <ArchiveConfirmDialog
        open={unarchiveDialogOpen}
        onClose={handleUnarchiveDialogClose}
        onConfirm={handleUnarchiveConfirm}
        error={unarchiveError}
        isArchive={false}
        className={
          classToUnarchive?.title?.en ||
          classToUnarchive?.title?.fr ||
          classToUnarchive?.title?.uk
        }
      />
    </>
  );
}
